'use client';

import { type MouseEventHandler, useEffect, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { useFeatureFlagEnabled } from '@frameio/feature-flags';
import { reportError } from '@frameio/telemetry';
import { auth, hasAccessTokenExpired } from '@frameio/utils/auth';
import { useHoverIntent } from '@frameio/vapor';
import { getDevFlag } from '../../dev-flags';

/**
 * FULL: Prefetch all links on mount
 * HYBRID: Prefetch grid on hover, everything else on mount
 * HOVER: Only ever prefetch on hover
 * OFF: No prefetching
 */
type PrefetchStrategy =
  | 'FULL'
  | 'HYBRID'
  | 'HOVER-ONLY-SENSITIVE'
  | 'HOVER-ONLY'
  | 'OFF';

const DEFAULT_STRATEGY: PrefetchStrategy = 'HOVER-ONLY';
const LESS_PREFETCHING_STRATEGY: PrefetchStrategy = 'OFF';
const MORE_PREFETCHING_STRATEGY: PrefetchStrategy = 'HOVER-ONLY-SENSITIVE';

/**
 * Order of precedence:
 * Local overrides -> Feature flags -> Default
 * Decrease flags -> Increase flags -> Default
 */
function usePrefetchStrategy(): PrefetchStrategy {
  const decreaseOverride = getDevFlag('PREFETCH_DECREASE');
  const increaseOverride = getDevFlag('PREFETCH_INCREASE');

  const decreaseFlag = useFeatureFlagEnabled('v4.route_prefetching_decrease');
  const increaseFlag = useFeatureFlagEnabled('v4.route_prefetching_increase');

  // Local overrides
  if (decreaseOverride) return LESS_PREFETCHING_STRATEGY;
  if (increaseOverride) return MORE_PREFETCHING_STRATEGY;

  // Feature flag
  if (decreaseFlag) return LESS_PREFETCHING_STRATEGY;
  if (increaseFlag) return MORE_PREFETCHING_STRATEGY;

  return DEFAULT_STRATEGY;
}

type PrefetchOpts = {
  isNextLink: boolean;
  placement: 'SINGLE' | 'LIST' | 'GRID';
};

type PrefetchProps = {
  onMouseMove?: MouseEventHandler;
  onMouseLeave?: MouseEventHandler;
  prefetch?: boolean;
};

/**
 * Unified prefetch strategy for any v4 links
 *
 * When a Next Link is available (most links), prefer
 * built in prefetch logic if it fits the strategy.
 * https://nextjs.org/docs/app/api-reference/components/link
 *
 * @param link for when we don't use built in prefetching
 * @param opts.isNextLink whether props are passed to a next/link component
 * @param opts.placement (single, list, grid), more numerous links may prefetch less
 *
 * @returns mouse handlers to prefetch, prefetch prop if next link
 */
function useAppPrefetch(
  link: string,
  { isNextLink, placement }: PrefetchOpts
): PrefetchProps {
  const router = useRouter();
  const strategy = usePrefetchStrategy();

  // On mount
  useEffect(() => {
    if (isNextLink) return; // Prefer prefetch prop

    // Avoid prefetching with expired access token
    if (hasAccessTokenExpired(auth.getAccessToken())) return;

    if (strategy === 'FULL') {
      router.prefetch(link);
    } else if (strategy === 'HYBRID' && placement !== 'GRID') {
      router.prefetch(link);
    }
  }, [strategy, router, link, isNextLink, placement]);

  // On Hover
  const { bind } = useHoverIntent(
    () => {
      if (isNextLink && !strategy.startsWith('HOVER-ONLY')) return; // Prefer prefetch prop

      // Avoid prefetching with expired access token
      if (hasAccessTokenExpired(auth.getAccessToken())) return;

      if (
        strategy.startsWith('HOVER-ONLY') ||
        (strategy === 'HYBRID' && placement === 'GRID')
      ) {
        router.prefetch(link);
      }
    },
    { velocity: strategy === 'HOVER-ONLY-SENSITIVE' ? 3 : 0.5 }
  );

  return useMemo(() => {
    const prefetchProp = isNextLink
      ? {
          prefetch: {
            FULL: true,
            HYBRID: placement !== 'GRID',
            'HOVER-ONLY': false,
            'HOVER-ONLY-SENSITIVE': false,
            OFF: false,
          }[strategy],
        }
      : {};

    return {
      ...prefetchProp,
      ...bind,
    };
  }, [isNextLink, placement, strategy, bind]);
}

/*
 * Prefetch is a supplemental feature, never crash
 * If this errors we are likely outside the main app
 */
const noop = {};
export function usePrefetch(link: string, opts: PrefetchOpts): PrefetchProps {
  try {
    return useAppPrefetch(link, opts);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Unexpected Prefetch Error', e);
    reportError(e, { message: 'Failed to mark transfer batches as viewed' });
    return noop;
  }
}
